import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Keeps a slider's thumb glued to the pointer while the editor sees at most one
 * commit per frame.
 *
 * A drag fires onChange on every pointermove — up to twice per frame — and each
 * commit rebuilds the graph and repaints the canvas, which on a text layer means
 * re-fitting the block against canvas metrics. Rendering `value` and committing
 * through `onChange` keeps the interaction fluid without dropping the last value:
 * `onChangeCommitted` flushes whatever is still pending.
 */
export const useSliderDragCommit = (commit) => {
  const [dragValue, setDragValue] = useState(null);
  const frameRef = useRef(0);
  const pendingRef = useRef(null);
  const commitRef = useRef(commit);
  commitRef.current = commit;

  const flush = useCallback(() => {
    frameRef.current = 0;
    const next = pendingRef.current;
    pendingRef.current = null;
    if (next == null) return;
    commitRef.current(next);
  }, []);

  const onChange = useCallback(
    (next) => {
      setDragValue(next);
      pendingRef.current = next;
      if (frameRef.current) return;
      frameRef.current = requestAnimationFrame(flush);
    },
    [flush]
  );

  const onChangeCommitted = useCallback(() => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    flush();
    setDragValue(null);
  }, [flush]);

  useEffect(
    () => () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    },
    []
  );

  return { dragValue, onChange, onChangeCommitted };
};
